import React from 'react';
import { Redirect, Switch } from 'react-router-dom';

import {
  CompanyOnboardingGuard,
  OnboardingGuard,
  RouteWithLayout
} from './components';
import { Minimal as MinimalLayout } from './layouts';
import MainLayout from './layouts/Main';
import LoadingAppSplash from './layouts/Main/components/LoadingAppSplash';

import {
  About as AboutView,
  AiComingSoon as AiComingSoonView,
  ApplicationScorecardPage as ApplicationScorecardPageView,
  ApplicationsKanbanBoard as ApplicationsKanbanBoardView,
  CandidateFAQ as CandidateFAQView,
  CandidateProfile as CandidateProfileView,
  CandidateProfileView as CandidateProfileViewPublic,
  CompanyFAQ as CompanyFAQView,
  CompanyOnboarding as CompanyOnboardingView,
  Interviews as InterviewsView,
  JobsList as JobsListView,
  JobsSearch as JobsSearchView,
  Messages as MessagesView,
  MyApplications as MyApplicationsView,
  NotFound as NotFoundView,
  Onboarding as OnboardingView,
  Plans as PlansView,
  Pricing as PricingView,
  Products as ProductsView,
  Settings as SettingsView,
  Startups as StartupsView,
  Suggestions as SuggestionsView,
  Support as SupportView,
  TermsAndConditions as TermsAndConditionsView,
  UserTypeSelection as UserTypeSelectionView
} from './views';
import AccountActivationWithRecaptcha from './views/AccountActivation/AccountActivationWithRecaptcha';
import ResendActivation from './views/ResendActivation';
import RegistrationSuccess from './views/RegistrationSuccess';
import ForgotPassword from './views/ForgotPassword';
import ResetPassword from './views/ResetPassword';
import MainPublic from './layouts/MainPublic';
import {
  APP_PATH_ABOUT,
  APP_PATH_ACTIVATION,
  APP_PATH_AI,
  APP_PATH_APPLICATIONS,
  APP_PATH_CANDIDATE_PROFILE,
  APP_PATH_COMPANY_ONBOARDING,
  APP_PATH_DASHBOARD,
  APP_PATH_EDITOR_COMPANY,
  APP_PATH_EDITOR_JOB,
  APP_PATH_FAQ_EMPLOYERS,
  APP_PATH_FAQ_JOB_SEEKERS,
  APP_PATH_FORGOT_PASSWORD,
  APP_PATH_INTERVIEWS,
  APP_PATH_JOB_ADDONS,
  APP_PATH_JOB_VIEW,
  APP_PATH_JOBS,
  APP_PATH_LOGIN,
  APP_PATH_MESSAGES,
  APP_PATH_MY_APPLICATIONS,
  APP_PATH_ONBOARDING,
  APP_PATH_PACKAGE_PLAN,
  APP_PATH_PLAN_CONFIGURE,
  APP_PATH_PRICING,
  APP_PATH_PRODUCTS,
  APP_PATH_REGISTRATION_SUCCESS,
  APP_PATH_RESEND_ACTIVATION,
  APP_PATH_RESET_PASSWORD,
  APP_PATH_ROOT,
  APP_PATH_SIGNUP,
  APP_PATH_STARTUPS,
  APP_PATH_SUCCESS_SUBSCRIPTION_PLAN,
  APP_PATH_SUGGESTIONS,
  APP_PATH_SUPPORT,
  APP_PATH_TERM_CONDITIONS,
  APP_PATH_USER_TYPE_SELECTION,
  APP_PATH_VIEW_CANDIDATE_PROFILE,
  APP_PATH_VIEW_COMPANY
} from './config';
import SecondMainPublic from './layouts/SecondMainPublic';
import JobViewPublic from './views/JobViewPublic/JobViewPublic';
import CompanyPublicView from './views/CompanyPublicView/CompanyPublicView';
import PropTypes from 'prop-types';
import SuccessSubscription from './views/SuccsesSuscription';
import SignUp from './views/SignUp';
import SignIn from './views/SignIn';
import DashboardView from './views/Dashboard/DashboardView';
import EditorJobView from './views/EditPreviewSections/EditorJobView';
import EditorCompanyView from './views/EditPreviewSections/EditorCompanyView';
import EditorPlanPackageView from './views/EditPreviewSections/EditorPlanPackageView';
import JobAddonsView from './views/JobAddons';
import PlanConfigurationView from './views/PlanConfiguration';
import { useCurrentUserInfo } from './hooks/useCurrentUserInfo';

const LoadLoginForAnonymous = ({ children, isLogin }) => {
  return isLogin ? children : <Redirect to={APP_PATH_LOGIN} />;
};
LoadLoginForAnonymous.propTypes = {
  isLogin: PropTypes.bool
};

const Routes = ({ isLogin, isLoadingApp }) => {
  const { user } = useCurrentUserInfo();
  const isCandidate = user?.roles?.includes('ROLE_CANDIDATE');
  const isCompany = user?.roles?.includes('ROLE_COMPANY');
  return isLoadingApp ? (
    <LoadingAppSplash open />
  ) : (
    <Switch>
      <RouteWithLayout
        component={() => <JobsListView showFilters />}
        exact
        layout={MainPublic}
        path={APP_PATH_ROOT}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_CANDIDATE']}>
            <OnboardingGuard isCandidate={isCandidate}>
              <JobsSearchView />
            </OnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_JOBS}
      />
      <RouteWithLayout
        component={StartupsView}
        exact
        layout={MainPublic}
        path={APP_PATH_STARTUPS}
      />
      <RouteWithLayout
        component={AiComingSoonView}
        exact
        layout={MainPublic}
        path={APP_PATH_AI}
      />
      <RouteWithLayout
        component={AboutView}
        exact
        layout={MainPublic}
        path={APP_PATH_ABOUT}
      />
      <RouteWithLayout
        component={TermsAndConditionsView}
        exact
        layout={SecondMainPublic}
        path={APP_PATH_TERM_CONDITIONS}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_COMPANY']}>
            <CompanyOnboardingGuard isCompany={isCompany}>
              <EditorJobView />
            </CompanyOnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={SecondMainPublic}
        path={APP_PATH_EDITOR_JOB}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin}>
            <CompanyOnboardingGuard isCompany={isCompany}>
              <DashboardView />
            </CompanyOnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_DASHBOARD}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin}>
            <ApplicationsKanbanBoardView />
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_APPLICATIONS}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin}>
            <ApplicationScorecardPageView />
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path="/applications/:applicationId/scorecard"
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_COMPANY']}>
            <CompanyOnboardingGuard isCompany={isCompany}>
              <ApplicationsKanbanBoardView />
            </CompanyOnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path="/applications/job/:jobId"
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_CANDIDATE']}>
            <OnboardingGuard isCandidate={isCandidate}>
              <MyApplicationsView />
            </OnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_MY_APPLICATIONS}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_CANDIDATE']}>
            <OnboardingGuard isCandidate={isCandidate}>
              <CandidateProfileView />
            </OnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_CANDIDATE_PROFILE}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_CANDIDATE']}>
            <OnboardingGuard isCandidate={isCandidate}>
              <SuggestionsView />
            </OnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_SUGGESTIONS}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin}>
            <SupportView />
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_SUPPORT}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_CANDIDATE']}>
            <OnboardingGuard isCandidate={isCandidate}>
              <OnboardingView />
            </OnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MinimalLayout}
        path={APP_PATH_ONBOARDING}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_COMPANY']}>
            <CompanyOnboardingView />
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MinimalLayout}
        path={APP_PATH_COMPANY_ONBOARDING}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin}>
            <CandidateProfileViewPublic />
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_VIEW_CANDIDATE_PROFILE}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin}>
            <MessagesView />
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_MESSAGES}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_COMPANY']}>
            <CompanyOnboardingGuard isCompany={isCompany}>
              <InterviewsView />
            </CompanyOnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path={APP_PATH_INTERVIEWS}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin} roles={['ROLE_COMPANY']}>
            <CompanyOnboardingGuard isCompany={isCompany}>
              <EditorCompanyView />
            </CompanyOnboardingGuard>
          </LoadLoginForAnonymous>
        )}
        exact
        layout={SecondMainPublic}
        path={APP_PATH_EDITOR_COMPANY}
      />
      <RouteWithLayout
        component={() => <JobViewPublic />}
        exact
        layout={SecondMainPublic}
        path={APP_PATH_JOB_VIEW}
      />
      <RouteWithLayout
        component={() => <CompanyPublicView />}
        exact
        layout={SecondMainPublic}
        path={APP_PATH_VIEW_COMPANY}
      />
      <RouteWithLayout
        component={SettingsView}
        exact
        layout={MainLayout}
        path="/settings"
      />
      <RouteWithLayout
        component={ProductsView}
        exact
        layout={MainPublic}
        path={APP_PATH_PRODUCTS}
      />
      <RouteWithLayout
        component={CompanyFAQView}
        exact
        layout={MainPublic}
        path={APP_PATH_FAQ_EMPLOYERS}
      />
      <RouteWithLayout
        component={CandidateFAQView}
        exact
        layout={MainPublic}
        path={APP_PATH_FAQ_JOB_SEEKERS}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin}>
            <PlansView />
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MainLayout}
        path="/plans"
      />
      <RouteWithLayout
        component={PricingView}
        exact
        layout={MainPublic}
        path={APP_PATH_PRICING}
      />
      <RouteWithLayout
        component={() => (
          <LoadLoginForAnonymous isLogin={isLogin}>
            <UserTypeSelectionView />
          </LoadLoginForAnonymous>
        )}
        exact
        layout={MinimalLayout}
        path={APP_PATH_USER_TYPE_SELECTION}
      />
      <RouteWithLayout
        component={SignUp}
        exact
        layout={MinimalLayout}
        path={APP_PATH_SIGNUP}
      />
      <RouteWithLayout
        component={SignIn}
        exact
        layout={MinimalLayout}
        path={APP_PATH_LOGIN}
      />
      <RouteWithLayout
        component={AccountActivationWithRecaptcha}
        exact
        layout={MinimalLayout}
        path={APP_PATH_ACTIVATION}
      />
      <RouteWithLayout
        component={ResendActivation}
        exact
        layout={MinimalLayout}
        path={APP_PATH_RESEND_ACTIVATION}
      />
      <RouteWithLayout
        component={RegistrationSuccess}
        exact
        layout={MinimalLayout}
        path={APP_PATH_REGISTRATION_SUCCESS}
      />
      <RouteWithLayout
        component={ForgotPassword}
        exact
        layout={MinimalLayout}
        path={APP_PATH_FORGOT_PASSWORD}
      />
      <RouteWithLayout
        component={ResetPassword}
        exact
        layout={MinimalLayout}
        path={APP_PATH_RESET_PASSWORD}
      />
      <RouteWithLayout
        component={NotFoundView}
        exact
        layout={MinimalLayout}
        path="/not-found"
      />
      <RouteWithLayout
        component={EditorPlanPackageView}
        exact
        layout={MinimalLayout}
        path={APP_PATH_PACKAGE_PLAN}
      />
      <RouteWithLayout
        component={JobAddonsView}
        exact
        layout={MinimalLayout}
        path={APP_PATH_JOB_ADDONS}
      />
      <RouteWithLayout
        component={PlanConfigurationView}
        exact
        layout={MainLayout}
        path={APP_PATH_PLAN_CONFIGURE}
      />
      <RouteWithLayout
        component={SuccessSubscription}
        exact
        layout={SecondMainPublic}
        path={APP_PATH_SUCCESS_SUBSCRIPTION_PLAN}
      />
      <Redirect to="/not-found" />
    </Switch>
  );
};

Routes.defaultProps = {
  isLogin: false,
  isLoadingApp: false
};

Routes.propTypes = {
  isLogin: PropTypes.bool,
  isLoadingApp: PropTypes.bool
};
export default Routes;
